import { useState } from "react";
import { Link } from "react-router-dom";
import { useDailySummary } from "./hooks";
import type { DailySummaryParams } from "./hooks";
import type { DailySummary } from "./api";
import { usePaymentMethods } from "../payment-methods/hooks";
import type { PaymentMethod } from "../payment-methods/api";
import { PageHeader } from "../../components/PageHeader";
import { ArrowDownIcon, ArrowUpIcon, BanknoteIcon, CalendarIcon, CreditCardIcon, NotebookPenIcon, ArrowLeftRightIcon } from "../../components/icons";
import { formatARS } from "../../lib/money";

type Preset = "today" | "yesterday" | "week" | "month" | "all" | "custom";

function todayLocalISO() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function previousDayLocalISO(today: string) {
  const date = new Date(`${today}T12:00:00`);
  date.setDate(date.getDate() - 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function monthStartLocalISO() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
}

function weekStartLocalISO(today: string) {
  const start = new Date(`${today}T12:00:00`);
  const daysSinceMonday = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - daysSinceMonday);
  return `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`;
}

// Suffix used across hero titles/section headings, one per period preset.
// "custom" falls back to "del día" or "del período" depending on whether
// the manually picked range collapses to a single day.
function periodLabel(preset: Preset, from: string, to: string): string {
  if (preset === "all") return "histórico";
  if (preset === "month") return "del mes";
  if (preset === "today") return "del día";
  if (preset === "yesterday") return "de ayer";
  if (preset === "week") return "del período";
  return from === to ? "del día" : "del período";
}

function filterHeadingLabel(preset: Preset): string {
  switch (preset) {
    case "today":
      return "Hoy";
    case "yesterday":
      return "Ayer";
    case "week":
      return "Esta semana";
    case "month":
      return "Este mes";
    case "all":
      return "Histórico";
    default:
      return "Rango personalizado";
  }
}

// Formatea un porcentaje con coma decimal (es-AR), un decimal fijo. Se usa
// tanto en la tarjeta "Comisiones" como en los tiles de medio de pago para
// que el % de comisión se vea consistente en toda la vista.
function formatPercentAR(value: number): string {
  return value.toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function MethodIcon({ color }: { color?: string }) {
  if (color === "cash") return <BanknoteIcon />;
  if (color === "transfer") return <ArrowLeftRightIcon />;
  if (color === "card") return <CreditCardIcon />;
  return <NotebookPenIcon />;
}

// Resuelve nombre y requiresNetReceived de un medio de pago con precedencia:
// 1) snapshot histórico (paymentMethodMeta) — es lo correcto para datos del
//    pasado, incluso si el medio fue desactivado o reemplazado después;
// 2) lista de medios activos (methodById) — cubre ids que solo aparecen en
//    movimientos de cuenta corriente, que no tienen snapshot;
// 3) fallback con el id crudo, para no romper el render si no aparece en
//    ninguna de las dos fuentes.
function resolvePaymentMethod(
  id: string | number,
  paymentMethodMeta: DailySummary["paymentMethodMeta"],
  methodById: Map<number, PaymentMethod>
): { name: string; requiresNetReceived: boolean } {
  const meta = paymentMethodMeta[String(id)];
  if (meta) return meta;
  const method = methodById.get(Number(id));
  if (method) return { name: method.name, requiresNetReceived: method.requiresNetReceived };
  return { name: `Medio #${id}`, requiresNetReceived: false };
}

export function DailySummaryView() {
  const today = todayLocalISO();
  const [preset, setPreset] = useState<Preset>("today");
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);

  const summaryParams: DailySummaryParams = preset === "all" ? { range: "all" } : { from, to };
  const summary = useDailySummary(summaryParams);
  const methods = usePaymentMethods();
  const methodById = new Map((methods.data ?? []).map((method) => [method.id, method]));

  const label = periodLabel(preset, from, to);

  const showToday = () => {
    setPreset("today");
    setFrom(today);
    setTo(today);
  };

  const showYesterday = () => {
    const yesterday = previousDayLocalISO(today);
    setPreset("yesterday");
    setFrom(yesterday);
    setTo(yesterday);
  };

  const showThisWeek = () => {
    setPreset("week");
    setFrom(weekStartLocalISO(today));
    setTo(today);
  };

  const showThisMonth = () => {
    setPreset("month");
    setFrom(monthStartLocalISO());
    setTo(today);
  };

  const showHistoric = () => {
    setPreset("all");
  };

  if (!summary.data) {
    return (
      <main className="page page--narrow">
        <PageHeader title="Resumen" />
        <section className="page-section">
          <FilterCard
            preset={preset}
            from={from}
            to={to}
            onFrom={(value) => {
              setPreset("custom");
              setFrom(value);
              if (value > to) setTo(value);
            }}
            onTo={(value) => {
              setPreset("custom");
              setTo(value);
              if (value < from) setFrom(value);
            }}
            onToday={showToday}
            onYesterday={showYesterday}
            onWeek={showThisWeek}
            onMonth={showThisMonth}
            onHistoric={showHistoric}
          />
          {summary.isLoading && <div className="skeleton-list"><span /><span /><span /></div>}
        </section>
      </main>
    );
  }

  const data = summary.data;
  const discounts = data.lineDiscountCents + data.globalDiscountCents + data.paymentMethodDiscountCents;
  // Canonical income/expense totals. Derived from the same fields the
  // backend uses to compute netDayCents, so Σ Ingresos − Σ Egresos always
  // matches the net figure exactly (the invariant the UI must prove).
  const incomeTotal = data.salesReceivedCents + data.consignmentAdvancesCents + data.cobrosCtaCteCents + data.manualIncomeCents + data.settlementsReceivedCents;
  const expenseTotal = data.consignmentRefundsCents + data.manualExpenseCents;
  // Commission % is informative only. The denominator comes straight from
  // the backend (commissionBaseCents), computed with the historical snapshot
  // of each sale's payment method — NOT the current state of methodById
  // (usePaymentMethods only returns active methods). Reconstructing it from
  // the current method list would drop sales made with a method that was
  // later deactivated or replaced (e.g. "Tarjeta" → "Tarjeta MP"), inflating
  // the % over 100% in the "Histórico" preset. discountPct is unrelated: it's
  // the discount granted TO the customer for paying with that method (e.g.
  // Efectivo/Transferencia often carry a discount, not a commission) — the
  // criterion that matters here is requiresNetReceived, same as the backend.
  const avgCommissionPct = data.commissionBaseCents > 0 ? (data.commissionCents / data.commissionBaseCents) * 100 : 0;

  // Cada sección del bloque Ingresos/Egresos se oculta por completo (título
  // incluido) cuando no tiene datos, en lugar de anunciar "sin X" — evita
  // mencionar movimientos que no ocurrieron en el período. Si TODAS las
  // secciones de una tarjeta quedan vacías, se reemplazan por un único
  // estado vacío a nivel tarjeta (ver showIncomeEmpty/showExpenseEmpty).
  const hasNetReceived = Object.entries(data.netReceivedByMethod).length > 0;
  const hasCobrosCtaCte = Object.entries(data.cobrosCtaCteByMethod).length > 0;
  const hasManualIncome = data.manualIncomeByConcept.length > 0;
  const hasSettlements = Object.entries(data.settlementsReceivedByMethod).length > 0;
  const hasManualExpense = data.manualExpenseByConcept.length > 0;
  const hasConsignmentAdvances = Object.entries(data.consignmentAdvancesByMethod).length > 0;
  const hasConsignmentRefunds = Object.entries(data.consignmentRefundsByMethod).length > 0;
  const showIncomeEmpty = !hasNetReceived && !hasConsignmentAdvances && !hasCobrosCtaCte && !hasManualIncome && !hasSettlements;
  const showExpenseEmpty = !hasConsignmentRefunds && !hasManualExpense;

  return (
    <main className="page page--narrow">
      <PageHeader title="Resumen" />
      <section className="page-section">
        <FilterCard
          preset={preset}
          from={from}
          to={to}
          onFrom={(value) => {
            setPreset("custom");
            setFrom(value);
            if (value > to) setTo(value);
          }}
          onTo={(value) => {
            setPreset("custom");
            setTo(value);
            if (value < from) setFrom(value);
          }}
          onToday={showToday}
          onYesterday={showYesterday}
          onWeek={showThisWeek}
          onMonth={showThisMonth}
          onHistoric={showHistoric}
        />

        <div className="summary-hero">
          <article className={`summary-hero--net${data.netDayCents < 0 ? " is-negative" : ""}`}>
            <span>Dinero neto {label}</span>
            <strong className="num">{formatARS(data.netDayCents)}</strong>
            <small>Cobros netos ingresados + otros ingresos − egresos</small>
          </article>
          <article className="summary-hero--sales">
            <span>Ventas {label}</span>
            <strong className="num">{formatARS(data.totalChargedCents)}</strong>
            <small>{data.salesCount} {data.salesCount === 1 ? "operación" : "operaciones"} · bruto {formatARS(data.grossSalesCents)}</small>
          </article>
        </div>

        <section className="summary-group summary-columns">
          <article className="summary-ledger">
            <div>
              <span className="ledger-icon ledger-icon--income"><ArrowUpIcon /></span>
              <div><p className="eyebrow">Ingresos</p><h2 className="num">{formatARS(incomeTotal)}</h2></div>
            </div>

            {showIncomeEmpty && (
              <div className="summary-ledger__empty">Sin ingresos en el período.</div>
            )}

            {hasNetReceived && (
              <div className="summary-ledger__section">
                <strong>Ventas cobradas</strong>
                <dl>
                  {Object.entries(data.netReceivedByMethod).map(([id, cents]) => {
                    const method = resolvePaymentMethod(id, data.paymentMethodMeta, methodById);
                    return (
                      <div key={id}>
                        <dt>{method.name}{method.requiresNetReceived && <span className="summary-ledger__hint"> · neto</span>}</dt>
                        <dd>{formatARS(cents)}</dd>
                      </div>
                    );
                  })}
                </dl>
              </div>
            )}

            {hasConsignmentAdvances && (
              <div className="summary-ledger__section">
                <strong>Anticipos de consignaciones</strong>
                <dl>
                  {Object.entries(data.consignmentAdvancesByMethod).map(([id, cents]) => (
                    <div key={id}>
                      <dt>{resolvePaymentMethod(id, data.paymentMethodMeta, methodById).name}</dt>
                      <dd>{formatARS(cents)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {hasCobrosCtaCte && (
              <div className="summary-ledger__section">
                <strong>Cobros de cuenta corriente</strong>
                <dl>
                  {Object.entries(data.cobrosCtaCteByMethod).map(([key, cents]) => (
                    <div key={key}>
                      <dt>{key === "none" ? "Sin especificar" : resolvePaymentMethod(key, data.paymentMethodMeta, methodById).name}</dt>
                      <dd>{formatARS(cents)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {hasManualIncome && (
              <div className="summary-ledger__section">
                <strong>Ingresos manuales</strong>
                <dl>
                  {data.manualIncomeByConcept.map((c) => (
                    // conceptId alone isn't unique: freeform concepts (no
                    // conceptId) group by their text, so multiple rows can
                    // share conceptId: null. Combine with conceptName instead.
                    <div key={`${c.conceptId ?? "none"}-${c.conceptName}`}>
                      <dt>{c.conceptName}</dt>
                      <dd>{formatARS(c.amountCents)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {hasSettlements && (
              <div className="summary-ledger__section">
                <strong>Cobrado por liquidaciones</strong>
                <dl>
                  {Object.entries(data.settlementsReceivedByMethod).map(([id, cents]) => (
                    <div key={id}>
                      <dt>{resolvePaymentMethod(id, data.paymentMethodMeta, methodById).name}</dt>
                      <dd>{formatARS(cents)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {data.pendingNetCents > 0 && (
              <div className="summary-ledger__notice">
                <strong className="num">{formatARS(data.pendingNetCents)}</strong> pendiente de confirmar · no suma hasta cargar el neto
              </div>
            )}

            {/* Pendiente de liquidación es un concepto distinto de "pendiente
                de neto": no es un cierre de día, solo indica que hay pagos de
                medios por liquidación (ej. ClubCred) esperando agruparse en
                /liquidaciones. Se distingue visualmente (info, no warning). */}
            {data.pendingSettlementCents > 0 && (
              <Link
                to="/liquidaciones"
                className="summary-ledger__notice summary-ledger__notice--settlement"
                style={{ display: "block", textDecoration: "none" }}
              >
                <strong className="num">{formatARS(data.pendingSettlementCents)}</strong> pendiente de liquidación · no suma hasta liquidar
              </Link>
            )}
          </article>

          <article className="summary-ledger">
            <div>
              <span className="ledger-icon ledger-icon--expense"><ArrowDownIcon /></span>
              <div><p className="eyebrow">Egresos</p><h2 className="num">{formatARS(expenseTotal)}</h2></div>
            </div>

            {showExpenseEmpty && (
              <div className="summary-ledger__empty">Sin egresos en el período.</div>
            )}

            {hasConsignmentRefunds && (
              <div className="summary-ledger__section">
                <strong>Devoluciones de consignaciones</strong>
                <dl>
                  {Object.entries(data.consignmentRefundsByMethod).map(([id, cents]) => (
                    <div key={id}>
                      <dt>{resolvePaymentMethod(id, data.paymentMethodMeta, methodById).name}</dt>
                      <dd>−{formatARS(cents)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {hasManualExpense && (
              <div className="summary-ledger__section">
                <strong>Egresos manuales</strong>
                <dl>
                  {data.manualExpenseByConcept.map((c) => (
                    <div key={`${c.conceptId ?? "none"}-${c.conceptName}`}>
                      <dt>{c.conceptName}</dt>
                      <dd>−{formatARS(c.amountCents)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </article>
        </section>

        <div className={`summary-reconcile-line${data.netDayCents < 0 ? " is-negative" : ""}`}>
          <span>Ingresos {formatARS(incomeTotal)} − Egresos {formatARS(expenseTotal)}</span>
          <strong className="num">{formatARS(data.netDayCents)}</strong>
        </div>

        <section className="summary-group">
          <div className="summary-group__heading"><div><p className="eyebrow">Cobros</p><h2>Medios de pago</h2></div></div>
          <div className="payment-breakdown">
            {Object.entries(data.totalChargedByMethod).length === 0 && <span className="summary-empty">Sin ventas registradas.</span>}
            {Object.entries(data.totalChargedByMethod).map(([id, cents]) => {
              // color/requiresCustomer only exist on active methods (no
              // historical snapshot for them yet), so those still read from
              // methodById; the display name uses the precedence helper.
              const method = methodById.get(Number(id));
              const name = resolvePaymentMethod(id, data.paymentMethodMeta, methodById).name;
              const received = data.netReceivedByMethod[Number(id)];
              // % de comisión solo cuando hay base calculable para este medio
              // (backend ya excluye ahí los pagos con neto todavía pendiente).
              // En el caso mixto (parte liquidada + parte pendiente) el %
              // y el "Neto ingresado" corresponden únicamente a la porción
              // liquidada, nunca al total cobrado (cents).
              const commissionBase = data.commissionBaseByMethod[id];
              const commissionSuffix =
                commissionBase > 0
                  ? ` · ${formatPercentAR(((data.commissionByMethod[id] ?? 0) / commissionBase) * 100)}% en comisiones`
                  : "";
              return (
                <article className={`payment-stat payment-${method?.color ?? "other"}`} key={id}>
                  <span><MethodIcon color={method?.color} /></span>
                  <div>
                    <small>{name}</small>
                    <strong className="num">{formatARS(cents)}</strong>
                    {method?.requiresCustomer ? (
                      <em>A cobrar · no ingresado</em>
                    ) : received === undefined ? (
                      <em>Neto pendiente de cargar</em>
                    ) : received !== cents ? (
                      <em>Neto ingresado {formatARS(received)}{commissionSuffix}</em>
                    ) : (
                      <em>Ingresado{commissionSuffix}</em>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="summary-group summary-detail-grid">
          <article className="summary-detail-card">
            <span>Descuentos otorgados</span>
            <strong className="num">−{formatARS(discounts)}</strong>
            {discounts > 0 && (
              <dl>
                <div><dt>Línea</dt><dd>{formatARS(data.lineDiscountCents)}</dd></div>
                <div><dt>General</dt><dd>{formatARS(data.globalDiscountCents)}</dd></div>
                <div><dt>Medio de pago</dt><dd>{formatARS(data.paymentMethodDiscountCents)}</dd></div>
              </dl>
            )}
          </article>

          <article className="summary-detail-card">
            <span>Comisiones <em>Informativo</em></span>
            <strong className="num">{formatARS(data.commissionCents)}</strong>
            <small>
              {data.commissionBaseCents > 0
                ? `${formatPercentAR(avgCommissionPct)}% promedio sobre lo cobrado con medios que descuentan comisión · ya está fuera del neto`
                : "Ya está fuera del neto."}
            </small>
            {data.settlementCommissionCents !== 0 && (
              <dl>
                {Object.entries(data.settlementCommissionByMethod).map(([id, cents]) => (
                  <div key={id}>
                    <dt>Liquidación · {resolvePaymentMethod(id, data.paymentMethodMeta, methodById).name}</dt>
                    <dd>{formatARS(cents)}</dd>
                  </div>
                ))}
              </dl>
            )}
          </article>
        </section>

        <section className="summary-group">
          <div className="summary-group__heading summary-group__heading--units"><div><p className="eyebrow">Productos</p><h2>Unidades vendidas</h2></div><strong className="summary-unit-total num">{data.unitsSold}</strong></div>
          <div className="summary-product-columns" key={`${preset}-${from}-${to}`}>
            <RankingCard
              title="Por producto"
              emptyLabel="Sin unidades vendidas."
              rows={data.unitsByProduct.map((p) => ({
                key: String(p.productId),
                label: p.productName,
                sublabel: `${formatARS(p.salesCents)} vendidos`,
                value: p.units,
              }))}
            />
            <RankingCard
              title="Por producto y talle"
              emptyLabel="Sin unidades vendidas."
              rows={data.unitsByProductSize.map((p) => ({
                key: `${p.productId}-${p.size ?? "none"}`,
                label: `${p.productName} · ${p.size ?? "Sin talle"}`,
                value: p.units,
              }))}
            />
            <RankingCard
              title="Por categoría"
              emptyLabel="Sin unidades vendidas."
              rows={data.unitsByCategory.map((c) => ({
                key: String(c.categoryId ?? "none"),
                label: c.categoryName,
                sublabel: `${formatARS(c.salesCents)} vendidos`,
                value: c.units,
              }))}
            />
          </div>
        </section>
      </section>
    </main>
  );
}

function FilterCard({
  preset,
  from,
  to,
  onFrom,
  onTo,
  onToday,
  onYesterday,
  onWeek,
  onMonth,
  onHistoric,
}: {
  preset: Preset;
  from: string;
  to: string;
  onFrom: (value: string) => void;
  onTo: (value: string) => void;
  onToday: () => void;
  onYesterday: () => void;
  onWeek: () => void;
  onMonth: () => void;
  onHistoric: () => void;
}) {
  const isHistoric = preset === "all";
  return (
    <div className="summary-filter-card">
      <div className="summary-filter-card__heading"><CalendarIcon width={20} height={20} /><div><strong>Período</strong><span>{filterHeadingLabel(preset)}</span></div></div>
      <div className="summary-date-range">
        <label className="date-control">
          <span>Desde</span>
          <input
            type="date"
            aria-label="Fecha desde"
            value={isHistoric ? "" : from}
            max={to}
            disabled={isHistoric}
            onChange={(event) => onFrom(event.target.value)}
          />
        </label>
        <span className="summary-date-range__separator" aria-hidden="true">→</span>
        <label className="date-control">
          <span>Hasta</span>
          <input
            type="date"
            aria-label="Fecha hasta"
            value={isHistoric ? "" : to}
            min={from}
            disabled={isHistoric}
            onChange={(event) => onTo(event.target.value)}
          />
        </label>
      </div>
      <div className="summary-quick-ranges">
        <button type="button" aria-pressed={preset === "today"} className={preset === "today" ? "is-active" : ""} onClick={onToday}>Hoy</button>
        <button type="button" aria-pressed={preset === "yesterday"} className={preset === "yesterday" ? "is-active" : ""} onClick={onYesterday}>Ayer</button>
        <button type="button" aria-pressed={preset === "week"} className={preset === "week" ? "is-active" : ""} onClick={onWeek}>Esta semana</button>
        <button type="button" aria-pressed={preset === "month"} className={preset === "month" ? "is-active" : ""} onClick={onMonth}>Este mes</button>
        <button type="button" aria-pressed={preset === "all"} className={preset === "all" ? "is-active" : ""} onClick={onHistoric}>Histórico</button>
      </div>
    </div>
  );
}

type RankingRow = { key: string; label: string; sublabel?: string; value: number };

// Top-10 ranking card with a "Ver todos" expansion. Used for the three
// "unidades vendidas" breakdowns (product, product+size, category). When a
// row has no `sublabel`, it renders as a flat single-line row (used by the
// product+size breakdown, which must NOT be grouped by product).
function RankingCard({ title, emptyLabel, rows }: { title: string; emptyLabel: string; rows: RankingRow[] }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, 10);

  return (
    <article className="summary-ranking">
      <h3>{title}</h3>
      {rows.length === 0 && <span className="summary-empty">{emptyLabel}</span>}
      {visible.map((row) => (
        <div className="summary-ranking__row" key={row.key}>
          <span><strong>{row.label}</strong>{row.sublabel && <small>{row.sublabel}</small>}</span>
          <b className="num">{row.value}</b>
        </div>
      ))}
      {!expanded && rows.length > 10 && (
        <button type="button" className="summary-ranking__more" onClick={() => setExpanded(true)}>
          Ver todos ({rows.length})
        </button>
      )}
      {expanded && rows.length > 10 && (
        <button type="button" className="summary-ranking__more" onClick={() => setExpanded(false)}>
          Ver menos
        </button>
      )}
    </article>
  );
}
